import { useState } from "react"
import { Search } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import { Badge } from "../components/ui/badge"
import { useLocale } from "../components/LocaleProvider"
import { useJobs, useJobStats, useClearPendingJobs } from "../lib/queries"
import type { Job } from "../types"
import type { TranslationKey } from "../i18n/translations"

const STATUS_VARIANT: Record<string, "info" | "success" | "warning" | "danger" | "neutral"> = {
  PENDING: "info",
  CLAIMED: "warning",
  COMPLETED: "success",
  FAILED: "danger",
}

const STATUS_KEY: Record<string, string> = {
  PENDING: "job_status_pending",
  CLAIMED: "job_status_claimed",
  COMPLETED: "job_status_completed",
  FAILED: "job_status_failed",
}

function Jobs() {
  const { t } = useLocale()
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("")

  const { data, isLoading } = useJobs({ status: statusFilter || undefined })
  const { data: stats } = useJobStats()
  const clearPending = useClearPendingJobs()

  const handleClearPending = () => {
    if (window.confirm(`Cancel all ${stats?.pending ?? 0} pending jobs? This cannot be undone.`)) {
      clearPending.mutate()
    }
  }

  const items: Job[] = data?.items ?? []
  const filtered = items.filter((row) =>
    Object.values(row).some((v) => String(v).toLowerCase().includes(search.toLowerCase())),
  )

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {stats && (
        <div className="grid grid-cols-5 gap-4">
          {[
            { label: "Total", value: stats.total, color: "text-foreground" },
            { label: t("job_status_pending"), value: stats.pending, color: "text-blue-600" },
            { label: t("job_status_claimed"), value: stats.claimed, color: "text-yellow-600" },
            { label: t("job_status_completed"), value: stats.completed, color: "text-green-600" },
            { label: t("job_status_failed"), value: stats.failed, color: "text-red-600" },
          ].map((s) => (
            <div key={s.label} className="rounded-lg border bg-card p-4">
              <div className="text-sm text-muted-foreground">{s.label}</div>
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("search_records")}
            className="h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">{t("filter_all_statuses")}</option>
          <option value="PENDING">{t("job_status_pending")}</option>
          <option value="CLAIMED">{t("job_status_claimed")}</option>
          <option value="COMPLETED">{t("job_status_completed")}</option>
          <option value="FAILED">{t("job_status_failed")}</option>
        </select>
        {stats && stats.pending > 0 && (
          <button
            onClick={handleClearPending}
            disabled={clearPending.isPending}
            className="h-10 px-4 rounded-md bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 disabled:opacity-50"
          >
            {clearPending.isPending ? "Clearing..." : `Cancel All Pending (${stats.pending})`}
          </button>
        )}
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">{t("th_id")}</TableHead>
              <TableHead>{t("th_full_name")}</TableHead>
              <TableHead>{t("th_profile_name")}</TableHead>
              <TableHead>{t("th_source")}</TableHead>
              <TableHead>{t("th_job_type")}</TableHead>
              <TableHead>{t("th_listing_status")}</TableHead>
              <TableHead>{t("th_worker")}</TableHead>
              <TableHead>{t("th_retries")}</TableHead>
              <TableHead>{t("th_error")}</TableHead>
              <TableHead>{t("th_listing_created")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-mono text-xs text-muted-foreground">{row.id}</TableCell>
                <TableCell className="max-w-[200px] truncate" title={row.listing?.title}>
                  {row.listing?.title || `#${row.listingId}`}
                </TableCell>
                <TableCell>{row.profile?.name || `#${row.profileId}`}</TableCell>
                <TableCell>
                  <Badge variant="neutral">{row.listing?.sourceName || "—"}</Badge>
                </TableCell>
                <TableCell className="text-xs">{row.type}</TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANT[row.status] || "neutral"}>
                    {t((STATUS_KEY[row.status] || row.status) as TranslationKey)}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-xs">{row.workerId || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{row.retries}</TableCell>
                <TableCell
                  className="text-xs text-destructive max-w-[150px] truncate"
                  title={row.error || ""}
                >
                  {row.error || "—"}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {new Date(row.createdAt).toLocaleString("de-DE")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default Jobs
